if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    #bound = false;
    #section;
    #submit;
    #form;
    #sizeRoot;
    #colorRoot;
    #button;
    #label;
    #price;
    #title;
    #titleSize;
    #image;
    #sizes;
    #colors;
    #productTitle;
    #submitObserver;
    #sizeObserver;
    #colorObserver;
    #visibilityObserver;
    #bundleObserver;
    #unsubscribeVariant;
    #onScrollCheck;
    #pendingVariant = false;
    #pendingLabel = '';
    #pendingTimer = null;

    connectedCallback() {
      if (this.#bound) return;
      this.#bound = true;
      queueMicrotask(() => this.#setup());
    }

    disconnectedCallback() {
      document.documentElement.style.setProperty('--sticky-atc-height', '0px');
      this.#bound = false;
      this.#submitObserver?.disconnect();
      this.#sizeObserver?.disconnect();
      this.#colorObserver?.disconnect();
      this.#visibilityObserver?.disconnect();
      this.#bundleObserver?.disconnect();
      this.#unsubscribeVariant?.();
      if (this.#onScrollCheck) window.removeEventListener('scroll', this.#onScrollCheck);
      this.#sizeRoot?.removeEventListener('change', this.#onSizeChange);
      this.#colorRoot?.removeEventListener('change', this.#onColorChange);
      this.#button?.removeEventListener('click', this.#onSubmitClick);
      window.clearTimeout(this.#pendingTimer);
      this.querySelectorAll('.sticky-atc__size').forEach((button) => {
        button.removeEventListener('click', this.#onSizeClick);
      });
      this.querySelectorAll('.sticky-atc__color').forEach((button) => {
        button.removeEventListener('click', this.#onColorClick);
      });
    }

    #setup() {
      this.hidden = false;
      this.removeAttribute('hidden');
      this.#section =
        document.getElementById(`shopify-section-${this.dataset.section}`) ||
        this.closest('.shopify-section') ||
        document;
      this.#submit = this.#section.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
      this.#form = document.getElementById(this.dataset.formId);
      this.#sizeRoot = this.#section.querySelector('.product-form__input--size');
      this.#colorRoot = this.#section.querySelector('.product-form__input--color-swatches, .product-form__input.main-Color, .product-form__input.main-Colour');
      this.#button = this.querySelector('.sticky-atc__submit');
      this.#label = this.querySelector('.sticky-atc__submit-label');
      this.#price = this.querySelector('[data-sticky-price]');
      this.#title = this.querySelector('.sticky-atc__title');
      this.#titleSize = this.querySelector('.sticky-atc__title-size');
      this.#image = this.querySelector('.sticky-atc__image');
      this.#sizes = this.querySelector('.sticky-atc__sizes');
      this.#colors = this.querySelector('.sticky-atc__colors');
      this.#productTitle = this.dataset.productTitle || '';

      if (!this.#submit || !this.#button || !this.#label) return;

      if (this.#isBundleApp()) {
        this.remove();
        return;
      }

      this.#syncSubmit();
      this.#syncSizes();
      this.#syncColors();

      this.#submitObserver = new MutationObserver(() => this.#syncSubmit());
      this.#submitObserver.observe(this.#submit, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });

      if (this.#sizeRoot) {
        this.#sizeRoot.addEventListener('change', this.#onSizeChange);
        this.#sizeObserver = new MutationObserver(() => this.#syncSizes());
        this.#sizeObserver.observe(this.#sizeRoot, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'checked', 'disabled', 'selected'],
        });
      }

      if (this.#colorRoot) {
        this.#colorRoot.addEventListener('change', this.#onColorChange);
        this.#colorObserver = new MutationObserver(() => this.#syncColors());
        this.#colorObserver.observe(this.#colorRoot, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'checked', 'disabled', 'selected'],
        });
      }

      this.#visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.#setVisible(passed);
      });
      this.#visibilityObserver.observe(this.#submit);

      this.#onScrollCheck = () => {
        const rect = this.#submit.getBoundingClientRect();
        this.#setVisible(rect.bottom < 0);
      };
      window.addEventListener('scroll', this.#onScrollCheck, { passive: true });
      this.#onScrollCheck();

      this.#button.addEventListener('click', this.#onSubmitClick);
      this.querySelectorAll('.sticky-atc__size').forEach((button) => {
        button.addEventListener('click', this.#onSizeClick);
      });
      this.querySelectorAll('.sticky-atc__color').forEach((button) => {
        button.addEventListener('click', this.#onColorClick);
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.#unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.#onVariantChange);
      }

      if (this.#form) {
        this.#bundleObserver = new MutationObserver(() => {
          if (this.#isBundleApp()) this.remove();
        });
        this.#bundleObserver.observe(this.#form, { childList: true, subtree: true });
      }
    }

    #isBundleApp() {
      return Boolean(
        this.#section.querySelector(
          '[data-simple-bundles-options], #simple-bundles-io-options, .simple-bundles, .pb__main'
        )
      );
    }

    #setVisible(passed) {
      this.classList.toggle('is-visible', passed);
      this.toggleAttribute('inert', !passed);
      this.setAttribute('aria-hidden', passed ? 'false' : 'true');
      this.#syncPageOffset();
    }

    #onSubmitClick = () => {
      if (!this.#submit || this.#submit.disabled) return;
      this.#submit.click();
    };

    #onSizeChange = () => {
      this.#setPendingVariant(true);
      this.#syncSizes();
    };

    #onColorChange = () => {
      this.#setPendingVariant(true);
      this.#syncColors();
    };

    #onVariantChange = (event) => {
      this.#setPendingVariant(false);
      const variant = event?.data?.variant;
      if (!variant) {
        this.#syncSubmit();
        return;
      }
      this.#syncImage(variant);
      this.#syncPrice(variant);
      this.#syncSizes();
      this.#syncColors();
      this.#syncSubmit();
    };

    #setPendingVariant(pending) {
      window.clearTimeout(this.#pendingTimer);

      if (pending) {
        this.#pendingLabel =
          this.#submit?.querySelector('span:not(.sold-out-message):not(.loading__spinner)')?.textContent?.trim() ||
          this.#label?.textContent?.trim() ||
          '';
        this.#pendingVariant = true;
        // Safety net if variantChange never fires (invalid option combo).
        this.#pendingTimer = window.setTimeout(() => this.#setPendingVariant(false), 3000);
      } else {
        this.#pendingVariant = false;
        this.#pendingLabel = '';
      }

      this.#syncSubmit();
    }

    #onSizeClick = (event) => {
      const value = event.currentTarget.dataset.value;
      if (!value || !this.#sizeRoot) return;

      const radio = this.#sizeRoot.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
      if (radio) {
        if (radio.checked) return;
        this.#setPendingVariant(true);
        this.#changeWithoutScrolling(() => {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
        return;
      }

      const select = this.#sizeRoot.querySelector('select');
      if (!select || select.value === value) return;
      this.#setPendingVariant(true);
      this.#changeWithoutScrolling(() => {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    };

    #onColorClick = (event) => {
      const value = event.currentTarget.dataset.value;
      if (!value || !this.#colorRoot) return;

      const radio = this.#colorRoot.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
      if (radio) {
        if (radio.checked) return;
        this.#setPendingVariant(true);
        this.#changeWithoutScrolling(() => {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
        return;
      }

      const select = this.#colorRoot.querySelector('select');
      if (!select || select.value === value) return;
      this.#setPendingVariant(true);
      this.#changeWithoutScrolling(() => {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    };

    #changeWithoutScrolling(changeVariant) {
      const scrollY = window.scrollY;
      const original = HTMLElement.prototype.scrollIntoView;
      HTMLElement.prototype.scrollIntoView = function () {};
      changeVariant();
      window.setTimeout(() => {
        HTMLElement.prototype.scrollIntoView = original;
        window.scrollTo(0, scrollY);
      }, 50);
    }

    #syncSubmit() {
      const submitLabelEl = this.#submit.querySelector(
        'span:not(.sold-out-message):not(.loading__spinner)'
      );
      const submitLabel = submitLabelEl?.textContent?.trim() || '';
      const unavailable = this.#submit.disabled || this.#submit.getAttribute('aria-disabled') === 'true';
      const submitting = this.#submit.classList.contains('loading');

      // Settle when the main ATC label updates (sold out / unavailable) without
      // waiting for variantChange — do not clear just because the button is still
      // enabled in the brief moment before toggleAddButton runs.
      if (this.#pendingVariant && submitLabel && submitLabel !== this.#pendingLabel) {
        window.clearTimeout(this.#pendingTimer);
        this.#pendingVariant = false;
        this.#pendingLabel = '';
      }

      // Variant fetch disables the main ATC before availability is known — show a
      // spinner instead of flashing "Sold out" during that window.
      const loading = submitting || this.#pendingVariant;
      const soldOut = unavailable && !loading;

      this.#button.disabled = unavailable || loading;
      this.#button.setAttribute('aria-disabled', unavailable || loading ? 'true' : 'false');
      this.#button.classList.toggle('loading', loading);
      this.#button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);

      if (loading) {
        this.#label.textContent = window.variantStrings?.addToCart || 'Add to cart';
        return;
      }

      this.#label.textContent = soldOut
        ? window.variantStrings?.soldOut || 'Sold out'
        : window.variantStrings?.addToCart || 'Add to cart';
    }

    #syncPageOffset() {
      const height = this.classList.contains('is-visible') ? `${this.offsetHeight}px` : '0px';
      document.documentElement.style.setProperty('--sticky-atc-height', height);
    }

    #syncImage(variant) {
      if (!this.#image || !variant?.featured_image) return;

      const image = variant.featured_image;
      const src = image.src.includes('?') ? `${image.src}&width=144` : `${image.src}?width=144`;

      if (this.#image.getAttribute('src') === src) return;
      this.#image.src = src;
      this.#image.srcset = `${src.replace('width=144', 'width=72')} 72w, ${src} 144w`;
    }

    #syncPrice(variant) {
      if (!this.#price || !variant) return;

      const price = this.#formatMoney(variant.price);
      const compare = variant.compare_at_price > variant.price ? this.#formatMoney(variant.compare_at_price) : '';

      this.#price.innerHTML = compare
        ? `<s class="sticky-atc__price-compare">${compare}</s><span class="sticky-atc__price-sale">${price}</span>`
        : `<span class="sticky-atc__price-regular">${price}</span>`;
    }

    #formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        const format = window.theme?.moneyFormat || window.cartStrings?.money_format;
        if (format) return window.Shopify.formatMoney(cents, format);
      }

      return `$${(Number(cents) / 100).toFixed(2)}`;
    }

    #syncTitle(selectedSize) {
      if (!this.#title) return;

      if (!selectedSize) {
        this.#title.textContent = this.#productTitle;
        return;
      }

      this.#title.innerHTML = `${this.#productTitle}<span class="sticky-atc__title-size"> · ${selectedSize}</span>`;
    }

    #syncSizes() {
      if (!this.#sizeRoot) {
        this.#syncTitle('');
        return;
      }

      const radios = this.#sizeRoot.querySelectorAll('input[type="radio"]');
      const select = this.#sizeRoot.querySelector('select');
      let selectedButton = null;
      let selectedValue = '';

      this.querySelectorAll('.sticky-atc__size').forEach((button) => {
        const value = button.dataset.value;
        let selected = false;
        let unavailable = false;

        if (radios.length) {
          const radio = this.#sizeRoot.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
          if (!radio) return;
          selected = radio.checked;
          unavailable = radio.classList.contains('disabled') || radio.classList.contains('sold-out-combo');
        } else if (select) {
          const option = Array.from(select.options).find((item) => item.value === value);
          if (!option) return;
          selected = option.selected;
          unavailable = option.disabled;
        }

        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-unavailable', unavailable);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        if (selected) {
          selectedButton = button;
          selectedValue = value;
        }
      });

      this.#syncTitle(selectedValue);

      if (selectedButton && this.#sizes) {
        this.#scrollSizeIntoView(selectedButton);
      }
    }

    #syncColors() {
      if (!this.#colorRoot || !this.#colors) return;

      const radios = this.#colorRoot.querySelectorAll('input[type="radio"]');
      const select = this.#colorRoot.querySelector('select');
      let selectedColorValue = '';

      this.querySelectorAll('.sticky-atc__color').forEach((button) => {
        const value = button.dataset.value;
        let selected = false;

        if (radios.length) {
          const radio = this.#colorRoot.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
          if (!radio) return;
          selected = radio.checked;
        } else if (select) {
          const option = Array.from(select.options).find((item) => item.value === value);
          if (!option) return;
          selected = option.selected;
        }

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        if (selected) selectedColorValue = value;
      });

      const label = this.querySelector('[data-sticky-color-label]');
      if (label && selectedColorValue) label.textContent = selectedColorValue.toUpperCase();
    }

    #scrollSizeIntoView(button) {
      if (!this.#sizes) return;

      const maxScroll = this.#sizes.scrollWidth - this.#sizes.clientWidth;
      if (maxScroll <= 0) return;

      const target = button.offsetLeft - this.#sizes.clientWidth / 2 + button.offsetWidth / 2;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#sizes.scrollTo({
        left: Math.max(0, Math.min(target, maxScroll)),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
